<template>
	<div class="w-100 d-flex flex-column ap-grey lighten-6 amelipro-page-layout">
		<div class="w-100 d-flex flex-column body-content">
			<nav
				aria-label="liens d'évitement"
				class="skip-link__nav"
			>
				<ul class="list-style-none skip-link__list">
					<li class="skip-link__item">
						<a
							class="skip-link pa-2 d-sr-only-focusable"
							href="#main-content"
						>
							{{ locales.skipLink }}
						</a>
					</li>

					<li
						v-for="(skipLink) in skipLinks"
						:key="skipLink.label"
						class="skip-link__item"
					>
						<a
							class="skip-link pa-2 d-sr-only-focusable"
							:href="skipLink.href"
						>
							{{ skipLink.label }}
						</a>
					</li>
				</ul>
			</nav>

			<slot name="header">
				<div
					v-if="ameliproPageLayoutInfos !== undefined && ameliproPageLayoutInfos.ameliproHeaderInfos !== undefined"
				>
					<AmeliproHeader
						:amelipro-header-infos="ameliproPageLayoutInfos.ameliproHeaderInfos"
						:backoffice="ameliproPageLayoutInfos.ameliproHeaderInfos.backoffice"
						:header-title="ameliproPageLayoutInfos.ameliproHeaderInfos.headerTitle"
						:service-name="ameliproPageLayoutInfos.ameliproHeaderInfos.serviceName"
						v-bind="$attrs"
						@back-btn-click="backBtnEvent"
					/>
				</div>
			</slot>

			<main
				id="main-content"
				class="amelipro-main"
				role="main"
				style="min-height: 1px"
			>
				<slot name="patientBanner">
					<div
						v-if="ameliproPageLayoutInfos !== undefined && ameliproPageLayoutInfos.ameliproPatientBannerInfos !== undefined && ameliproPageLayoutInfos.displayPatientBanner !== false"
					>
						<AmeliproPatientBanner
							:birth-name="ameliproPageLayoutInfos.ameliproPatientBannerInfos.birthName"
							:birthdate="ameliproPageLayoutInfos.ameliproPatientBannerInfos.birthdate"
							:more-information-href="ameliproPageLayoutInfos.ameliproPatientBannerInfos.moreInformationHref"
							:more-information-to="ameliproPageLayoutInfos.ameliproPatientBannerInfos.moreInformationTo"
							:name="ameliproPageLayoutInfos.ameliproPatientBannerInfos.name"
							:no-more-information="ameliproPageLayoutInfos.ameliproPatientBannerInfos.noMoreInformation"
							:no-patient-change="ameliproPageLayoutInfos.ameliproPatientBannerInfos.noPatientChange"
							:patient-doctor-infos="ameliproPageLayoutInfos.ameliproPatientBannerInfos.patientDoctorInfos"
							:patient-nir="ameliproPageLayoutInfos.ameliproPatientBannerInfos.patientNir"
							:patient-organism="ameliproPageLayoutInfos.ameliproPatientBannerInfos.patientOrganism"
							:patient-status="ameliproPageLayoutInfos.ameliproPatientBannerInfos.patientStatus"
							:patient-system="ameliproPageLayoutInfos.ameliproPatientBannerInfos.patientSystem"
							@click:patient-change="emitPatientChangeEvent"
						/>
					</div>
				</slot>

				<div
					class="mx-auto main-content__content"
					:style="customMainContent ? undefined : style"
				>
					<slot name="main" />
				</div>
			</main>

			<div class="mt-auto">
				<slot name="footer">
					<div
						v-if="ameliproPageLayoutInfos !== undefined && ameliproPageLayoutInfos.ameliproFooterInfos !== undefined"
					>
						<AmeliproFooter
							:a11y-compliance="ameliproPageLayoutInfos.ameliproFooterInfos.a11yCompliance"
							:a11y-href="ameliproPageLayoutInfos.ameliproFooterInfos.a11yHref"
							:a11y-to="ameliproPageLayoutInfos.ameliproFooterInfos.a11yTo"
							:about-href="ameliproPageLayoutInfos.ameliproFooterInfos.aboutHref"
							:about-to="ameliproPageLayoutInfos.ameliproFooterInfos.aboutTo"
							:back-office="ameliproPageLayoutInfos.ameliproFooterInfos.backOffice"
							:back-office-text="ameliproPageLayoutInfos.ameliproFooterInfos.backOfficeText"
							:cgu-href="ameliproPageLayoutInfos.ameliproFooterInfos.cguHref"
							:cgu-to="ameliproPageLayoutInfos.ameliproFooterInfos.cguTo"
							:configuration-href="ameliproPageLayoutInfos.ameliproFooterInfos.configurationHref"
							:configuration-to="ameliproPageLayoutInfos.ameliproFooterInfos.configurationTo"
							:legal-notice-href="ameliproPageLayoutInfos.ameliproFooterInfos.legalNoticeHref"
							:legal-notice-to="ameliproPageLayoutInfos.ameliproFooterInfos.legalNoticeTo"
							:no-a11y="ameliproPageLayoutInfos.ameliproFooterInfos.noA11y"
							:no-about="ameliproPageLayoutInfos.ameliproFooterInfos.noAbout"
							:no-cgu="ameliproPageLayoutInfos.ameliproFooterInfos.noCgu"
							:no-configuration="ameliproPageLayoutInfos.ameliproFooterInfos.noConfiguration"
							:no-legal-notice="ameliproPageLayoutInfos.ameliproFooterInfos.noLegalNotice"
							:no-link-a11y="ameliproPageLayoutInfos.ameliproFooterInfos.noLinkA11y"
							:no-site-map="ameliproPageLayoutInfos.ameliproFooterInfos.noSiteMap"
							:site-map-href="ameliproPageLayoutInfos.ameliproFooterInfos.siteMapHref"
							:site-map-to="ameliproPageLayoutInfos.ameliproFooterInfos.siteMapTo"
							:version="ameliproPageLayoutInfos.ameliproFooterInfos.version"
						/>
					</div>
				</slot>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { AmeliproPageLayoutInfos, SkipLink } from './types';
	import { PropType, computed } from 'vue';
	import AmeliproFooter from '../AmeliproFooter/AmeliproFooter.vue';
	import AmeliproHeader from '../AmeliproHeader/AmeliproHeader.vue';
	import AmeliproPatientBanner from '../AmeliproPatientBanner/AmeliproPatientBanner.vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IndexedObject } from '../../types';
	import { locales } from './locales';

	defineProps({
		ameliproPageLayoutInfos: {
			type: Object as PropType<AmeliproPageLayoutInfos>,
			default: undefined,
		},
		customMainContent: {
			type: Boolean,
			default: false,
		},
		skipLinks: {
			type: Array as PropType<SkipLink[]>,
			default: () => [],
		},
	});

	const breakpointHelper = new BreakpointHelper();

	const style = computed<IndexedObject>(() => {
		const styles: IndexedObject = {
			maxWidth: '100%',
			padding: '30px 10px',
		};

		if (breakpointHelper.width >= 1072) {
			styles.maxWidth = '980px';
			styles.padding = '30px 0';
		}

		if (breakpointHelper.width >= 1240) {
			styles.maxWidth = '1144px';
			styles.padding = '30px 0';
		}
		return styles;
	});

	const emit = defineEmits(['back-btn-click', 'click:patient-change']);

	const backBtnEvent = (): void => {
		emit('back-btn-click');
	};

	const emitPatientChangeEvent = (): void => {
		emit('click:patient-change');
	};
</script>

<style lang="scss" scoped>
.body-content {
	position: relative;
	height: auto;
	min-height: 100vh;
}

.skip-link {
	position: absolute;
	top: 0;
	left: 0;
	color: $ap-blue-darken1;
	background-color: $ap-white;
	text-decoration: underline;
	z-index: 10;
}
</style>
